import React, { useEffect, useReducer, useState, useCallback } from "react";
import { API, graphqlOperation } from "aws-amplify";
import NumberFormat from "react-number-format";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardContent from "@material-ui/core/CardContent";
import Container from "@material-ui/core/Container";
import Divider from "@material-ui/core/Divider";
import Drawer from "@material-ui/core/Drawer";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import MobileStepper from "@material-ui/core/MobileStepper";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import TextField from "@material-ui/core/TextField";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import Toolbar from "@material-ui/core/Toolbar";
import Typography from "@material-ui/core/Typography";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Skeleton from "@material-ui/lab/Skeleton";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import CloseIcon from "@material-ui/icons/Close";
import LocalShippingOutlinedIcon from "@material-ui/icons/LocalShippingOutlined";
import RoomOutlinedIcon from "@material-ui/icons/RoomOutlined";
import ShoppingCartOutlinedIcon from "@material-ui/icons/ShoppingCartOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import ForumIcon from "@material-ui/icons/Forum";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import analytics from "../analytics";
import { withContext } from "../context";
import logger from "../logger";
import {
  CART_STATUS,
  DELIVERY_OPTIONS,
  ORDER_STATUS,
  PAYMENT_OPTIONS,
  TEMPLATES
} from "../constants";
import {
  formatCelular,
  getPaymentInfo,
  nombreEsValido,
  removeEmptyStringElements,
  toTitleCase,
  unformatCelular,
  validateEmail
} from "../functions";
import { createClient, updateClient, createCart, updateCart } from "../graphql/mutations";
import { getClient } from "../graphql/queries";
import { createTransaction } from "../libs/Payku";
import Loading from "./Loading";
import Currency from "./Currency";
import ImageLoader from "./ImageLoader";
import PaymentButton from "./PaymentButton";
import SelectAddress from "./SelectAddress";

const useStyles = makeStyles(theme => ({
  deliveryServiceTitle: {
    fontSize: "1.15rem"
  },
  alert: {
    width: "100%"
  },
  title: {
    flexGrow: 1
  },
  container: {
    paddingTop: "64px"
  },
  deliveryAndPayment: {
    width: "calc(100vw - 300px)",
    background: theme.palette.background.paper
  },
  summary: {
    width: "300px",
    height: "calc(100vh - 58px)",
    padding: theme.spacing(4),
    position: "fixed",
    top: "58px",
    right: 0,
    background: theme.palette.background.default,
    borderLeft: "1px solid",
    borderLeftColor: theme.palette.divider,
    zIndex: 0
  },
  section: {
    margin: theme.spacing(4, 0),

    "& .payment-options": {
      paddingBottom: 0
    }
  },
  singleProductItem: {
    marginTop: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  productMedia: {
    width: "60px",
    height: "60px"
  },
  summaryTotal: {
    padding: theme.spacing(1, 0),
    borderTop: "1px solid",
    borderTopColor: theme.palette.divider,
    borderBottom: "1px solid",
    borderBottomColor: theme.palette.divider
  },
  strong: {
    fontWeight: "500"
  }
}));

export const defaultDeliveryConvenienceText =
  "Una vez realizada la compra, debes contactar al vendedor para acordar el método de envío.";

const initialState = {
  email: "",
  firstname: "",
  lastname: "",
  phone: "",
  delivery: {
    option: DELIVERY_OPTIONS.convenience,
    deliveryAddress: {
      addressName: "",
      addressNumber: "",
      addressComuna: ""
    },
    deliveryAddress2: "",
    deliveryNote: "",
    deliveryService: null,
    convenienceNote: "",
    pickupOption: "self",
    pickupPerson: "",
    pickupNote: ""
  },
  paymentOption: ""
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case "email":
    case "firstname":
    case "lastname":
    case "phone":
    case "paymentOption":
    case "delivery":
      return { ...state, [type]: payload };
    case "deliveryOption":
      return { ...state, delivery: { ...state.delivery, option: payload } };
    case "deliveryService":
      return {
        ...state,
        delivery: { ...state.delivery, deliveryService: payload }
      };
    case "deliveryAddress":
      return {
        ...state,
        delivery: { ...state.delivery, deliveryAddress: payload, deliveryAddress2: "" }
      };
    case "load":
      const object = {
        ...state,
        firstname: payload.firstname,
        lastname: payload.lastname,
        phone: payload.phone,
        delivery: {
          ...initialState.delivery,
          option: state.delivery.option,
          deliveryAddress: payload.address,
          deliveryAddress2: payload.address2
        },
        paymentOption: ""
      };
      return object;
    case "reset":
      return initialState;
    default:
      return { ...state };
  }
};

const stepsKeys = {
  client: "client",
  delivery: "delivery",
  payment: "payment",
  summary: "summary"
};

const steps = [
  { key: stepsKeys.client, title: "Datos personales" },
  { key: stepsKeys.delivery, title: "Entrega" },
  { key: stepsKeys.payment, title: "Medio de pago" },
  { key: stepsKeys.summary, title: "Resumen del pedido" }
];

const deliveryTabs = {
  homeDelivery: { value: 0, label: "Entrega a domicilio" },
  convenience: { value: 1, label: "A convenir" },
  storePickup: { value: 2, label: "Retiro en tienda" }
};

const PaymentWizard = ({
  mobile,
  template,
  store,
  comunas,
  useStoreSlug,
  items,
  total,
  open,
  onClose,
  onCompleted
}) => {
  const classes = useStyles();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [sending, setSending] = useState(false);
  const [step, setStep] = useState(0);
  const [deliveryTab, setDeliveryTab] = useState(deliveryTabs.homeDelivery.value);
  const [remoteClient, setRemoteClient] = useState(null);
  const [subtotal, setSubtotal] = useState(0);
  const [totalFinal, setTotalFinal] = useState(0);
  const [cart, setCart] = useState(null);
  const [deliveryAddressGoogle, setDeliveryAddressGoogle] = useState(null);
  const [noDeliveryServices, setNoDeliveryServices] = useState(false);
  const [deliveryServices, setDeliveryServices] = useState([]);
  const [fetchingDeliveryServices, setFetchingDeliveryServices] = useState(false);

  const fetchDeliveryOptions = useCallback(
    async comuna => {
      setFetchingDeliveryServices(true);
      let options = [];
      try {
        const init = {
          queryStringParameters: {
            store: store.slug,
            comuna
          }
        };

        const result = await API.get("carritofacilapi", "/store/deliveryOptions", init);

        if (result.error) {
          logger("PaymentWizard.fetchDeliveryOptions", result.error);
        } else {
          options = result.options;
        }
      } catch (err) {
        logger("PaymentWizard.fetchDeliveryOptions", err);
      } finally {
        setFetchingDeliveryServices(false);
        setNoDeliveryServices(options.length === 0);
        setDeliveryServices(options);
      }
    },
    [store.slug]
  );

  useEffect(() => {
    if (open) {
      if (items && items.length > 0) {
        let hasDelivery = false;

        for (let i = 0; i < items.length; i++) {
          const item = items[i];

          if (
            item.product.body.withDelivery ||
            typeof item.product.body.withDelivery === "undefined"
          ) {
            hasDelivery = true;
            break;
          }
        }

        let deliveryOption = DELIVERY_OPTIONS.none;

        if (hasDelivery) {
          if (Boolean(store.body.config.deliveryOptions)) {
            if (store.body.config.deliveryOptions.deliveryPickup) {
              deliveryOption = DELIVERY_OPTIONS.pickup;
            }

            if (store.body.config.deliveryOptions.deliveryConvenience) {
              deliveryOption = DELIVERY_OPTIONS.convenience;
            }

            if (store.body.config.deliveryOptions.deliveryHome) {
              deliveryOption = DELIVERY_OPTIONS.delivery;
            }
          } else {
            deliveryOption = DELIVERY_OPTIONS.convenience;
          }
        }

        dispatch({
          type: "deliveryOption",
          payload: deliveryOption
        });
      }
    }
  }, [open, items, store.body.config]);

  useEffect(() => {
    setSubtotal(total);

    if (state.delivery.option === DELIVERY_OPTIONS.delivery && state.delivery.deliveryService) {
      setTotalFinal(subtotal + state.delivery.deliveryService.fee);
    } else {
      setTotalFinal(total);
    }
  }, [total, subtotal, store, state.delivery.option, state.delivery.deliveryService]);

  useEffect(() => {
    const validateGoogleAddress = googleAddress => {
      let result = { valid: true, msg: "" };

      try {
        const { terms } = googleAddress;
        const addressNumber = terms[1].value;
        const addressComuna = terms.slice(-2).reverse().pop().value;

        if (terms.length !== 4) {
          result = {
            valid: false,
            msg:
              "Al parecer la dirección no tiene el formato 'calle, número, comuna, país'. Favor busca nuevamente tu dirección o completa los datos manualmente"
          };
        } else if (!Number.isInteger(parseInt(addressNumber))) {
          result = {
            valid: false,
            msg:
              "Al parecer la dirección no contiene un número de calle. Favor busca nuevamente tu dirección o completa los datos manualmente"
          };
        } else if (comunas.includes(addressComuna)) {
          result = {
            valid: false,
            msg:
              "No hemos podido reconocer la comuna segun la dirección indicada. Favor completa los datos manualmente"
          };
        }
      } catch (err) {
        logger("PaymentWizard.validateGoogleAddress", err);
        result = {
          valid: false,
          msg: "No hemos podido reconocer la dirección, favor completar los datos manualmente"
        };
      } finally {
        return result;
      }
    };

    if (deliveryAddressGoogle) {
      let addressName = "";
      let addressNumber = "";
      let addressComuna = "";

      try {
        const { terms } = deliveryAddressGoogle;
        const validGoogleAddress = validateGoogleAddress(deliveryAddressGoogle);

        if (validGoogleAddress.valid) {
          addressName = terms[0].value;
          addressNumber = terms[1].value;
          addressComuna = terms.slice(-2).reverse().pop().value;
        } else {
          addressName = terms[0].value;
          addressNumber = "";
          addressComuna = "";
        }
      } catch (err) {
        addressName = "";
        addressNumber = "";
        addressComuna = "";
        logger("PaymentWizard.deliveryAddressGoogle", err);
      } finally {
        const payload = { addressName, addressNumber, addressComuna };
        dispatch({ type: "deliveryAddress", payload });
        setDeliveryAddressGoogle(null);
      }
    }
  }, [deliveryAddressGoogle, comunas]);

  useEffect(() => {
    if (state.delivery.deliveryAddress && state.delivery.deliveryAddress.addressComuna) {
      const selectedComuna = state.delivery.deliveryAddress.addressComuna;
      fetchDeliveryOptions(selectedComuna);
    }

    dispatch({
      type: "deliveryService",
      payload: null
    });
  }, [state.delivery.deliveryAddress, fetchDeliveryOptions]);

  const fetchClient = async () => {
    setSending(true);

    let client = null;

    try {
      const vars = {
        email: state.email
      };

      const result = await API.graphql(graphqlOperation(getClient, vars));

      client = result.data.getClient;

      setRemoteClient(client);
    } catch (err) {
      logger("PaymentWizard.fetchingClient.error", err);
    }

    setSending(false);

    return client;
  };

  const handleClose = () => {
    onClose();
    setStep(0);
    dispatch({ type: "reset" });
  };

  const newClient = async () => {
    try {
      const vars = {
        input: {
          email: state.email
        }
      };

      const result = await API.graphql(graphqlOperation(createClient, vars));

      setRemoteClient(result.data.createClient);

      dispatch({ type: "load", payload: result.data.createClient });
      dispatch({
        type: "load",
        payload: {
          firstname: "",
          lastname: "",
          phone: "",
          address: initialState.delivery.deliveryAddress,
          address2: ""
        }
      });
    } catch (err) {
      logger("PaymentWizard.newClient.error", err);
    }
  };

  const saveClient = prop => {
    if (prop && remoteClient && (remoteClient[prop] !== state[prop] || prop === "addresses")) {
      const input = {
        email: state.email
      };

      switch (prop) {
        case "address":
          input[prop] = JSON.stringify(state.delivery.deliveryAddress);
          break;
        case "address2":
          input[prop] = state.delivery.deliveryAddress2;
          break;
        case "addresses":
          input.address = JSON.stringify(state.delivery.deliveryAddress);
          input.address2 = state.delivery.deliveryAddress2;
          break;
        default:
          input[prop] = state[prop];
          break;
      }

      const vars = { input };

      API.graphql(graphqlOperation(updateClient, vars))
        .then(result => setRemoteClient(result.data.updateClient))
        .catch(err => logger("PaymentWizard.saveClient.error", { prop, err }));
    }
  };

  const saveCart = async (cartID, params) => {
    let operation = "";
    let graphql = null;
    let vars = null;

    if (!cartID) {
      vars = {
        input: {
          createdAt: new Date().toISOString(),
          status: CART_STATUS.pending,
          storeID: store.id,
          clientID: state.email,
          items: JSON.stringify(items),
          ...params
        }
      };

      operation = "createCart";
      graphql = createCart;
    } else {
      vars = {
        input: {
          id: cartID,
          ...params
        }
      };

      operation = "updateCart";
      graphql = updateCart;
    }

    try {
      const result = await API.graphql(graphqlOperation(graphql, vars));

      if (result.data[operation]) {
        setCart(result.data[operation]);
        return result.data[operation];
      } else {
        logger(`PaymentWizard.saveCart.result.${operation}`, result);
      }
    } catch (err) {
      logger(`PaymentWizard.saveCart.${operation}.error`, err);
    }

    return null;
  };

  const validateClient = () => {
    return (
      nombreEsValido(state.firstname) &&
      nombreEsValido(state.lastname) &&
      state.phone.length === 8 &&
      validateEmail(state.email)
    );
  };

  const validateGoogleAddress = googleAddress => {
    let result = { valid: true, msg: "" };

    try {
      const { terms } = googleAddress;
      const addressNumber = terms[1].value;
      const addressComuna = terms.slice(-2).reverse().pop().value;

      if (terms.length !== 4) {
        result = {
          valid: false,
          msg:
            "Al parecer la dirección no tiene el formato 'calle, número, comuna, país'. Favor busca nuevamente tu dirección o completa los datos manualmente"
        };
      } else if (!Number.isInteger(parseInt(addressNumber))) {
        result = {
          valid: false,
          msg:
            "Al parecer la dirección no contiene un número de calle. Favor busca nuevamente tu dirección o completa los datos manualmente"
        };
      } else if (comunas.includes(addressComuna)) {
        result = {
          valid: false,
          msg:
            "No hemos podido reconocer la comuna segun la dirección indicada. Favor completa los datos manualmente"
        };
      }
    } catch (err) {
      logger("PaymentWizard.validateGoogleAddress", err);
      result = {
        valid: false,
        msg: "No hemos podido reconocer la dirección, favor completar los datos manualmente"
      };
    } finally {
      return result;
    }
  };

  const validateHomeDelivery = () => {
    return (
      state.delivery.deliveryAddress &&
      state.delivery.deliveryAddress.description !== "" &&
      state.delivery.deliveryService !== null
    );
  };

  const validatePickup = () => {
    return (
      state.delivery.pickupOption === "self" ||
      (state.delivery.pickupOption === "other" && state.delivery.pickupPerson !== "")
    );
  };

  const validateConvenience = () => {
    return true;
  };

  const validateDelivery = () => {
    switch (state.delivery.option) {
      case DELIVERY_OPTIONS.delivery:
        return validateHomeDelivery();
      case DELIVERY_OPTIONS.pickup:
        return validatePickup();
      case DELIVERY_OPTIONS.convenience:
        return validateConvenience();
      case DELIVERY_OPTIONS.none:
        return true;
      default:
        return false;
    }
  };

  const validatePayment = () => {
    return state.paymentOption !== "";
  };

  const validate = () => {
    if (!mobile && template === TEMPLATES.ecommerce) {
      switch (steps[step].key) {
        case stepsKeys.client:
          return validateClient();
        case stepsKeys.delivery:
          return validateDelivery() && validatePayment();
        default:
          return false;
      }
    } else {
      switch (steps[step].key) {
        case stepsKeys.client:
          return validateClient();
        case stepsKeys.delivery:
          return validateDelivery();
        case stepsKeys.payment:
          return validatePayment();
        case stepsKeys.summary:
          return true;
        default:
          return false;
      }
    }
  };

  const getDeliveryDetails = () => {
    let delivery = null;

    switch (state.delivery.option) {
      case DELIVERY_OPTIONS.delivery:
        const { deliveryAddress, deliveryAddress2, deliveryNote, deliveryService } = state.delivery;
        delivery = {
          deliveryAddress,
          deliveryAddress2,
          deliveryNote,
          deliveryService
        };
        break;
      case DELIVERY_OPTIONS.pickup:
        const { pickupOption, pickupPerson, pickupNote } = state.delivery;
        delivery = {
          pickupTerms: store.body.config.deliveryPickupText,
          pickupOption,
          pickupPerson,
          pickupNote
        };
        break;
      case DELIVERY_OPTIONS.convenience:
        delivery = {
          description: state.delivery.convenienceNote
        };
        break;
      case DELIVERY_OPTIONS.none:
        delivery = {
          description: "Sin delivery"
        };
        break;
      default:
        break;
    }

    removeEmptyStringElements(delivery);

    return delivery;
  };

  const createOrder = (carrito, status) => {
    const cliente = {
      primer_nombre: state.firstname,
      apellido_paterno: state.lastname,
      telefono: state.phone,
      email: state.email,
      direccion: remoteClient.address || null,
      direccion2: remoteClient.address2 || null
    };

    const despacho = {
      tipo: state.delivery.option,
      ...getDeliveryDetails()
    };

    const tienda = {
      id: store.id,
      slug: store.slug,
      nombre: store.name,
      url: `${window.location.origin}${useStoreSlug ? `/${store.slug}` : ""}`,
      email: store.body.config.email
    };

    const pago = {
      subtotal,
      total: totalFinal,
      medioPago: getPaymentInfo(state.paymentOption)
    };

    const order = {
      origen: `${window.location.origin}${useStoreSlug ? `/${store.slug}` : ""}`,
      estado: status,
      cliente,
      despacho,
      carrito,
      tienda,
      pago
    };

    return order;
  };

  const send = async (order, cartID, status) => {
    setSending(true);

    try {
      const response = await API.post("carritoticketsapi", "/tickets/venta", {
        body: order
      });

      if (response && response.success) {
        const ticket = response.ticket;

        if (ticket.success) {
          saveCart(cartID, {
            orderNumber: `${ticket.folio}-${store.code}`,
            ticket: JSON.stringify(ticket),
            status
          });

          const products = [];
          items.forEach(i => products.push(i.productID));
          await analytics.products(products, analytics.metrics.products.venta);

          return ticket;
        }
      } else {
        throw response;
      }
    } catch (err) {
      logger("PaymentWizard.sending.error", err);
      setSending(false);
    }

    return null;
  };

  const processPayment = async () => {
    const delivery = {
      type: state.delivery.option,
      details: JSON.stringify(getDeliveryDetails())
    };

    const payment = {
      type: getPaymentInfo(state.paymentOption).type
    };

    const paymentDetails = {
      subtotal,
      total: totalFinal
    };

    if (state.delivery.option === DELIVERY_OPTIONS.delivery && state.delivery.deliveryService) {
      paymentDetails.deliveryFee = state.delivery.deliveryService.fee;
    } else {
      paymentDetails.deliveryFee = 0;
    }

    payment.details = JSON.stringify(paymentDetails);

    let keepLoading = false;

    setSending(true);

    const currentCart = await saveCart(cart ? cart.id : null, {
      delivery,
      payment
    });

    let ticket = null;

    if (currentCart) {
      if (state.paymentOption === PAYMENT_OPTIONS.contraentrega) {
        const order = createOrder(currentCart, ORDER_STATUS.inbox);

        if (currentCart.ticket) {
          if (typeof currentCart.ticket === "string") {
            ticket = JSON.parse(currentCart.ticket);
          } else {
            ticket = currentCart.ticket;
          }
        } else {
          ticket = await send(order, currentCart.id, CART_STATUS.finished);
        }

        if (ticket) {
          onCompleted(`comprobante/${cart.id}`);
        } else {
          logger("PaymentWizard.processPayment.ticket.notFound");
        }
      } else if (state.paymentOption === PAYMENT_OPTIONS.transferencia) {
        const order = createOrder(currentCart, ORDER_STATUS.paymentPending);

        if (currentCart.ticket) {
          if (typeof currentCart.ticket === "string") {
            ticket = JSON.parse(currentCart.ticket);
          } else {
            ticket = currentCart.ticket;
          }
        } else {
          ticket = await send(order, currentCart.id, CART_STATUS.onPayment);
        }

        if (ticket) {
          keepLoading = true;

          setTimeout(() => {
            onCompleted(`transferencia/${currentCart.id}`);
          }, 1500);
        } else {
          logger("PaymentWizard.processPayment.ticket.notFound");
        }
      } else {
        const order = createOrder(currentCart, ORDER_STATUS.paymentPending);

        if (currentCart.ticket) {
          if (typeof currentCart.ticket === "string") {
            ticket = JSON.parse(currentCart.ticket);
          } else {
            ticket = currentCart.ticket;
          }
        } else {
          ticket = await send(order, currentCart.id, CART_STATUS.pending);
        }

        if (ticket) {
          const subject = `Compra ${ticket.folio} en ${store.name}`;

          const transaction = await createTransaction({
            order: `${ticket.folio}-${store.code}`,
            subject,
            amount: totalFinal,
            paymentOption: state.paymentOption,
            marketplaceId: store.body.config.marketplaceId,
            urlReturn: `${window.location.origin}${
              useStoreSlug ? `/${store.slug}` : ""
            }/verificacion/${currentCart.id}`,
            paramsNotify: `cartID=${currentCart.id}`
          });

          if (transaction.id) {
            paymentDetails.transactionID = transaction.id;
            paymentDetails.timestamp = new Date().toISOString();
            paymentDetails.url = transaction.url;

            payment.details = JSON.stringify(paymentDetails);

            const updated = await saveCart(currentCart.id, {
              status: CART_STATUS.onPayment,
              payment,
              transactionID: transaction.id
            });

            if (updated) {
              if (transaction.url) {
                keepLoading = true;

                setTimeout(() => {
                  window.location.href = transaction.url;
                }, 1500);
              }
            } else {
              logger("PaymentWizard.processPayment.transaction.cart.error");
            }
          } else {
            logger("PaymentWizard.processPayment.transaction.error", transaction);
          }
        } else {
          logger("PaymentWizard.processPayment.ticket.error");
        }
      }
    } else {
      logger("PaymentWizard.processPayment.save.error");
    }

    if (!keepLoading) {
      setSending(false);
    }
  };

  const emailChanged = async () => {
    if (validateEmail(state.email)) {
      const client = await fetchClient();

      if (client) {
        let address = {
          addressName: "",
          addressNumber: "",
          addressComuna: ""
        };

        let clientAddress = client.address && JSON.parse(client.address);

        if (clientAddress) {
          if (clientAddress.description) {
            const addressGoogle = clientAddress;

            const addressName = addressGoogle.terms[0].value;
            const addressNumber = parseInt(addressGoogle.terms[1].value)
              ? addressGoogle.terms[1].value
              : "";
            const addressComuna = addressGoogle.terms.slice(-2).reverse().pop().value;

            address = {
              addressName,
              addressNumber,
              addressComuna
            };
          } else {
            address = clientAddress;
          }
        }

        const payload = {
          firstname: client.firstname || "",
          lastname: client.lastname || "",
          phone: client.phone || "",
          address,
          address2: client.address2 || ""
        };
        dispatch({ type: "load", payload });
      } else {
        await newClient();
      }

      setDeliveryServices([]);
    }
  };

  const deliveryChanged = delivery => {
    dispatch({
      type: "deliveryOption",
      payload: delivery
    });
  };

  const next = () => {
    if (!mobile && template === TEMPLATES.ecommerce) {
      if (steps[step].key === stepsKeys.delivery) {
        if (state.delivery.option === DELIVERY_OPTIONS.delivery) {
          saveClient("addresses");
        }
        processPayment();
      } else {
        setStep(s => ++s);
      }
    } else {
      switch (steps[step].key) {
        case stepsKeys.client:
        case stepsKeys.payment:
          setStep(s => ++s);
          break;
        case stepsKeys.delivery:
          if (state.delivery.option === DELIVERY_OPTIONS.delivery) {
            saveClient("addresses");
          }
          setStep(s => ++s);
          break;
        case stepsKeys.summary:
          processPayment();
          break;
        default:
          break;
      }
    }
  };

  const renderClient = () => {
    return (
      <>
        <Typography className="mt-2" variant="h5" align="center">
          Hola, necesitamos algunos datos de ti
        </Typography>

        <TextField
          fullWidth
          className="mt-4"
          label="Email"
          type="email"
          disabled={sending}
          value={state.email}
          onChange={({ target }) =>
            dispatch({ type: "email", payload: target.value.toLowerCase() })
          }
          onBlur={emailChanged}
          inputProps={{
            maxLength: 255
          }}
        />

        <TextField
          fullWidth
          className="mt-3"
          label="Nombre"
          disabled={sending}
          value={state.firstname}
          onChange={({ target }) =>
            dispatch({ type: "firstname", payload: toTitleCase(target.value) })
          }
          onBlur={() => saveClient("firstname")}
          inputProps={{
            maxLength: 50
          }}
        />

        <TextField
          fullWidth
          className="mt-3"
          label="Apellido"
          disabled={sending}
          value={state.lastname}
          onChange={({ target }) =>
            dispatch({ type: "lastname", payload: toTitleCase(target.value) })
          }
          onBlur={() => saveClient("lastname")}
          inputProps={{
            maxLength: 50
          }}
        />

        <TextField
          fullWidth
          className="mt-3"
          label="Teléfono"
          disabled={sending}
          value={formatCelular(state.phone)}
          onChange={({ target }) =>
            dispatch({
              type: "phone",
              payload: unformatCelular(target.value)
            })
          }
          onBlur={() => saveClient("phone")}
          inputProps={{
            maxLength: 15
          }}
        />
      </>
    );
  };

  const renderNoDelivery = () => {
    return (
      <>
        <Typography className="mt-4" variant="h6" align="center" color="textSecondary">
          {items.length === 1
            ? "El producto seleccionado no tiene delivery en Carrito Fácil. "
            : "Los productos seleccionados no tienen delivery en Carrito Fácil. "}
          Debe coordinar la entrega con el vendedor.
        </Typography>
      </>
    );
  };

  const renderHomeDelivery = () => {
    return (
      <Grid className="mt-4" container spacing={3}>
        <Grid item xs={12} className="mb-4">
          <SelectAddress
            resetOnSelect
            className="w-100"
            label="Buscar dirección"
            placeholder="Ej: Irarrázaval 2551, Ñuñoa, Chile"
            disabled={sending}
            onChange={place => setDeliveryAddressGoogle(place)}
          />
        </Grid>

        <Grid item xs={12} sm={7}>
          <TextField
            fullWidth
            label="Calle / Avenida"
            placeholder="Ej: Irarrázaval"
            value={state.delivery.deliveryAddress.addressName}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              maxLength: 255
            }}
            onChange={({ target }) =>
              dispatch({
                type: "delivery",
                payload: {
                  ...state.delivery,
                  deliveryAddress: {
                    ...state.delivery.deliveryAddress,
                    addressName: target.value ? toTitleCase(target.value) : ""
                  }
                }
              })
            }
          />
        </Grid>
        <Grid item xs={12} sm={5}>
          <TextField
            fullWidth
            label="Número"
            placeholder="Ej: 2551"
            value={state.delivery.deliveryAddress.addressNumber}
            InputLabelProps={{
              shrink: true
            }}
            inputProps={{
              maxLength: 255
            }}
            onChange={({ target }) =>
              dispatch({
                type: "delivery",
                payload: {
                  ...state.delivery,
                  deliveryAddress: {
                    ...state.delivery.deliveryAddress,
                    addressNumber: target.value ? target.value : ""
                  }
                }
              })
            }
          />
        </Grid>

        <Grid item xs={12} sm={7}>
          <TextField
            fullWidth
            label="Casa / Depto / Oficina / Edificio / Piso"
            placeholder="Ej: Edificio A, Depto 1001"
            disabled={sending}
            value={state.delivery.deliveryAddress2}
            InputLabelProps={{
              shrink: true
            }}
            onChange={({ target }) =>
              dispatch({
                type: "delivery",
                payload: {
                  ...state.delivery,
                  deliveryAddress2: target.value ? toTitleCase(target.value) : ""
                }
              })
            }
            inputProps={{
              maxLength: 255
            }}
          />
        </Grid>

        <Grid item xs={12} sm={5}>
          <Autocomplete
            fullWidth
            clearOnBlur
            options={comunas}
            value={state.delivery.deliveryAddress.addressComuna || ""}
            getOptionLabel={option => (option.nombre ? option.nombre : option)}
            renderInput={params => (
              <TextField
                {...params}
                placeholder="Ej: Ñuñoa"
                label="Comuna"
                InputLabelProps={{
                  shrink: true
                }}
              />
            )}
            getOptionSelected={(option, value) => {
              return option.nombre ? option.nombre === value : option === value;
            }}
            onChange={(_, newValue) => {
              dispatch({
                type: "delivery",
                payload: {
                  ...state.delivery,
                  deliveryAddress: {
                    ...state.delivery.deliveryAddress,
                    addressComuna: newValue && newValue.nombre ? newValue.nombre : ""
                  }
                }
              });
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            className="mt-3"
            label="Nota de entrega (opcional)"
            placeholder="Ej: Dejar en conserjería"
            disabled={sending}
            value={state.delivery.deliveryNote}
            InputLabelProps={{
              shrink: true
            }}
            onChange={({ target }) =>
              dispatch({
                type: "delivery",
                payload: { ...state.delivery, deliveryNote: target.value }
              })
            }
            inputProps={{
              maxLength: 500
            }}
          />
        </Grid>

        {fetchingDeliveryServices && (
          <Grid item xs={12}>
            <Skeleton />

            <List>
              {[1, 2, 3].map((_, index) => (
                <ListItem key={`loading-option-${index}`} divider>
                  <ListItemText>
                    <Skeleton width="50%" />
                    <Skeleton width="20%" />
                    <Skeleton width="40%" />
                  </ListItemText>
                </ListItem>
              ))}
            </List>
          </Grid>
        )}
        {!fetchingDeliveryServices && noDeliveryServices && (
          <Grid item xs={12}>
            <Alert severity="info" className={classes.alert}>
              <strong>Atención:</strong> No hay servicio de despacho disponible para esa dirección.
            </Alert>
          </Grid>
        )}
        {!fetchingDeliveryServices && deliveryServices.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h6">Elige el tipo de entrega</Typography>

            <List>
              {deliveryServices.map((option, index) => (
                <ListItem
                  key={`delivery-option-${index}`}
                  selected={option.name === (state.delivery.deliveryService || {}).name}
                  button
                  divider
                  onClick={() =>
                    dispatch({
                      type: "deliveryService",
                      payload: option
                    })
                  }
                >
                  <ListItemText>
                    <Typography variant="body1" className={classes.deliveryServiceTitle}>
                      {option.name}
                    </Typography>
                    {option.fee > 0 && (
                      <Typography variant="body2" color="textSecondary">
                        <NumberFormat
                          value={option.fee}
                          thousandSeparator="."
                          decimalSeparator=","
                          prefix="$"
                          displayType={"text"}
                        />
                      </Typography>
                    )}
                    <Typography variant="body2" color="textSecondary">
                      {option.conditions}
                    </Typography>
                  </ListItemText>
                  <ListItemSecondaryAction>
                    <ChevronRightIcon color="primary" />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderConvenience = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography className="mt-4" variant="h6">
            Términos y condiciones de entrega
          </Typography>
          <Typography variant="body2">
            {store.body.config.deliveryConvenienceText || defaultDeliveryConvenienceText}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            className="mt-4"
            label="Comentarios de envío (recomendado)"
            placeholder="Ej: Enviar a sucursal Chilexpress"
            disabled={sending}
            value={state.delivery.convenienceNote}
            InputLabelProps={{
              shrink: true
            }}
            onChange={({ target }) =>
              dispatch({
                type: "delivery",
                payload: { ...state.delivery, convenienceNote: target.value }
              })
            }
            inputProps={{
              maxLength: 300
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderStorePickup = () => {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography className="mt-4" variant="h6">
            Términos y condiciones de entrega
          </Typography>
          <Typography variant="body2">{store.body.config.deliveryPickupText}</Typography>
        </Grid>

        <Grid item xs={12}>
          <FormControl className="mt-4" component="fieldset">
            <FormLabel component="label">¿Quien retira el pedido?</FormLabel>

            <RadioGroup
              row
              aria-label="position"
              name="position"
              defaultValue={state.delivery.pickupOption}
              onChange={event =>
                dispatch({
                  type: "delivery",
                  payload: {
                    ...state.delivery,
                    pickupOption: event.target.value
                  }
                })
              }
            >
              <FormControlLabel
                value="self"
                control={<Radio size="small" color="primary" />}
                label="Yo"
              />
              <FormControlLabel
                value="other"
                control={<Radio size="small" color="primary" />}
                label="Alguien retira por mi"
              />
            </RadioGroup>

            {state.delivery.pickupOption === "other" && (
              <TextField
                fullWidth
                label="Nombre de persona que retira *"
                placeholder="Ej: Juan Pérez"
                disabled={sending}
                value={state.delivery.pickupPerson}
                InputLabelProps={{
                  shrink: true
                }}
                onChange={({ target }) =>
                  dispatch({
                    type: "delivery",
                    payload: { ...state.delivery, pickupPerson: target.value }
                  })
                }
                inputProps={{
                  maxLength: 100
                }}
              />
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Nota para retiro (opcional)"
            placeholder="Ej: Iré mañana por la tarde"
            disabled={sending}
            value={state.delivery.pickupNote}
            InputLabelProps={{
              shrink: true
            }}
            onChange={({ target }) =>
              dispatch({
                type: "delivery",
                payload: { ...state.delivery, pickupNote: target.value }
              })
            }
            inputProps={{
              maxLength: 300
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderDelivery = () => {
    if (state.delivery.option === DELIVERY_OPTIONS.none) {
      return renderNoDelivery();
    }

    return (
      <>
        {store.body.config.deliveryOptions &&
          Number(store.body.config.deliveryOptions.deliveryHome) +
            Number(store.body.config.deliveryOptions.deliveryConvenience) +
            Number(store.body.config.deliveryOptions.deliveryPickup) >
            1 && (
            <Tabs
              variant="scrollable"
              indicatorColor="secondary"
              value={deliveryTab}
              onChange={(_, newValue) => setDeliveryTab(newValue)}
            >
              {store.body.config.deliveryOptions.deliveryHome && (
                <Tab
                  label={deliveryTabs.homeDelivery.label}
                  onClick={() => deliveryChanged(DELIVERY_OPTIONS.delivery)}
                />
              )}

              {store.body.config.deliveryOptions.deliveryConvenience && (
                <Tab
                  label={deliveryTabs.convenience.label}
                  onClick={() => deliveryChanged(DELIVERY_OPTIONS.convenience)}
                />
              )}

              {store.body.config.deliveryOptions.deliveryPickup && (
                <Tab
                  label={deliveryTabs.storePickup.label}
                  onClick={() => deliveryChanged(DELIVERY_OPTIONS.pickup)}
                />
              )}
            </Tabs>
          )}

        {state.delivery.option === DELIVERY_OPTIONS.delivery && renderHomeDelivery()}
        {state.delivery.option === DELIVERY_OPTIONS.convenience && renderConvenience()}
        {state.delivery.option === DELIVERY_OPTIONS.pickup && renderStorePickup()}
      </>
    );
  };

  const renderPayment = preventNext => {
    const hasValidPaymentOptions =
      Boolean(store.body.config.marketplaceId) ||
      (store.body.config.paymentOptions.includes(PAYMENT_OPTIONS.transferencia) &&
        Boolean(store.body.config.recaudacion));

    if (hasValidPaymentOptions) {
      return (
        <div className="payment-options">
          {store.body.config.paymentOptions.map((option, index) => {
            switch (option) {
              case PAYMENT_OPTIONS.payku_webpay:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.payku_webpay}
                    selected={state.paymentOption}
                    onClick={() => {
                      dispatch({
                        type: "paymentOption",
                        payload: PAYMENT_OPTIONS.payku_webpay
                      });
                      if (!preventNext) next();
                    }}
                  />
                );
              case PAYMENT_OPTIONS.payku_onepay:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.payku_onepay}
                    selected={state.paymentOption}
                    onClick={() => {
                      dispatch({
                        type: "paymentOption",
                        payload: PAYMENT_OPTIONS.payku_onepay
                      });
                      if (!preventNext) next();
                    }}
                  />
                );
              case PAYMENT_OPTIONS.contraentrega:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.contraentrega}
                    selected={state.paymentOption}
                    onClick={() => {
                      dispatch({
                        type: "paymentOption",
                        payload: PAYMENT_OPTIONS.contraentrega
                      });
                      if (!preventNext) next();
                    }}
                  />
                );
              case PAYMENT_OPTIONS.payku_khipu:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.payku_khipu}
                    selected={state.paymentOption}
                    onClick={() => {
                      dispatch({
                        type: "paymentOption",
                        payload: PAYMENT_OPTIONS.payku_khipu
                      });
                      if (!preventNext) next();
                    }}
                  />
                );
              case PAYMENT_OPTIONS.transferencia:
                return (
                  <PaymentButton
                    key={`payment-${index}`}
                    option={PAYMENT_OPTIONS.transferencia}
                    selected={state.paymentOption}
                    onClick={() => {
                      dispatch({
                        type: "paymentOption",
                        payload: PAYMENT_OPTIONS.transferencia
                      });
                      if (!preventNext) next();
                    }}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      );
    } else {
      return (
        <Typography className="mt-4" variant="h6" align="center" color="textSecondary">
          No hay medios de pagos habilitados
        </Typography>
      );
    }
  };

  const renderSummary = () => {
    const payment = getPaymentInfo(state.paymentOption, state.paymentOption);

    return (
      <>
        {sending && <Loading fullscreen onTop />}

        <div className="summary">
          <Typography className="mt-2" variant="h6" align="center">
            Favor revisa el detalle de tu compra!
          </Typography>

          <Card className="mt-2 summary-products" elevation={0}>
            <CardActionArea onClick={onClose}>
              <CardContent className="d-flex justify-content-between">
                <div className="d-flex align-items-center">
                  <ShoppingCartOutlinedIcon fontSize="large" />
                  <div className="d-flex flex-column ml-3">
                    <Typography variant="body1">
                      {items.length} Producto{items.length !== 1 && "s"}
                    </Typography>
                  </div>
                </div>

                <div className="d-flex flex-column justify-content-center">
                  <ArrowForwardIosIcon size="large" />
                </div>
              </CardContent>
            </CardActionArea>
          </Card>

          <Divider className="mt-2" variant="fullWidth" />

          <Card className="summary-delivery" elevation={0}>
            <CardActionArea onClick={() => setStep(1)}>
              <CardContent className="d-flex justify-content-between">
                {state.delivery.option === DELIVERY_OPTIONS.none && (
                  <div className="d-flex align-items-center">
                    <ReportProblemOutlinedIcon fontSize="large" />
                    <div className="d-flex flex-column ml-3">
                      <Typography variant="body1">Producto sin delivery</Typography>
                      <Typography variant="body2" color="textSecondary">
                        Debe coordinar la entrega con el vendedor
                      </Typography>
                    </div>
                  </div>
                )}
                {state.delivery.option === DELIVERY_OPTIONS.delivery &&
                  state.delivery.deliveryService && (
                    <div className="d-flex align-items-center">
                      <LocalShippingOutlinedIcon fontSize="large" />
                      <div className="d-flex flex-column ml-3">
                        <Typography variant="body1">Entrega a domicilio</Typography>
                        <Typography variant="body2">
                          {state.delivery.deliveryService.name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {`${state.delivery.deliveryAddress.addressName} ${state.delivery.deliveryAddress.addressNumber}, ${state.delivery.deliveryAddress.addressComuna}`}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {state.delivery.deliveryAddress2}
                        </Typography>
                      </div>
                    </div>
                  )}
                {state.delivery.option === DELIVERY_OPTIONS.convenience && (
                  <div className="d-flex align-items-center">
                    <ForumIcon fontSize="large" />
                    <div className="d-flex flex-column ml-3">
                      <Typography variant="body1">A convenir</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {state.delivery.convenienceNote
                          ? state.delivery.convenienceNote
                          : defaultDeliveryConvenienceText}
                      </Typography>
                    </div>
                  </div>
                )}
                {state.delivery.option === DELIVERY_OPTIONS.pickup && (
                  <div className="d-flex align-items-center">
                    <RoomOutlinedIcon fontSize="large" />
                    <div className="d-flex flex-column ml-3">
                      <Typography variant="body1">Retiro en tienda</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {store.body.config.deliveryPickupText}
                      </Typography>
                      {state.delivery.pickupNote && (
                        <Typography variant="body2" color="textSecondary">
                          {state.delivery.pickupNote}
                        </Typography>
                      )}
                      {state.delivery.pickupOption === "other" && state.delivery.pickupPerson && (
                        <Typography variant="body2" color="textSecondary">
                          Retirará {state.delivery.pickupPerson}
                        </Typography>
                      )}
                    </div>
                  </div>
                )}
                <div className="d-flex flex-column justify-content-center">
                  <ArrowForwardIosIcon size="large" />
                </div>
              </CardContent>
            </CardActionArea>
          </Card>

          <Divider variant="fullWidth" />

          <Card className="summary-payment" elevation={0}>
            <CardActionArea onClick={() => setStep(2)}>
              <CardContent className="d-flex justify-content-between">
                <div className="d-flex align-items-center">
                  {payment.icon}
                  {payment.subtitle ? (
                    <div className="d-flex flex-column ml-3">
                      <Typography variant="body1">{payment.title}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {payment.subtitle}
                      </Typography>
                    </div>
                  ) : (
                    <Typography className="ml-3" variant="body1">
                      {payment.title}
                    </Typography>
                  )}
                </div>
                <div className="d-flex flex-column justify-content-center">
                  <ArrowForwardIosIcon size="large" />
                </div>
              </CardContent>
            </CardActionArea>
          </Card>

          <Divider variant="fullWidth" />

          <div className="summary-total">
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2">Subtotal</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" align="right">
                  <Currency value={subtotal} />
                </Typography>
              </Grid>
              {state.delivery.option === DELIVERY_OPTIONS.delivery &&
                state.delivery.deliveryService &&
                state.delivery.deliveryService.fee > 0 && (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="body2">Despacho</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" align="right">
                        <Currency value={state.delivery.deliveryService.fee} />
                      </Typography>
                    </Grid>
                  </>
                )}
              <Grid item xs={6}>
                <Typography variant="h6">Total del pedido</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="h6" align="right">
                  <Currency value={totalFinal} />
                </Typography>
              </Grid>
            </Grid>
          </div>
        </div>
      </>
    );
  };

  const renderSteps = () => {
    switch (steps[step].key) {
      case stepsKeys.client:
        return renderClient();
      case stepsKeys.delivery:
        return renderDelivery();
      case stepsKeys.payment:
        return renderPayment(false);
      case stepsKeys.summary:
        return renderSummary();
      default:
        return null;
    }
  };

  const renderButtonText = () => {
    if (sending) return "Enviando";

    if (steps[step].key !== stepsKeys.summary) {
      return "Continuar";
    } else {
      if (state.paymentOption === PAYMENT_OPTIONS.contraentrega) {
        return (
          <NumberFormat
            value={totalFinal}
            thousandSeparator="."
            decimalSeparator=","
            prefix="Realizar pedido $"
            displayType={"text"}
          />
        );
      } else {
        return (
          <NumberFormat
            value={totalFinal}
            thousandSeparator="."
            decimalSeparator=","
            prefix="Realizar pago $"
            displayType={"text"}
          />
        );
      }
    }
  };

  const renderButton = () => {
    return (
      <Button
        className="my-5 mx-auto wide d-block"
        variant="contained"
        size="large"
        disabled={!validate() || sending}
        onClick={next}
      >
        {renderButtonText()}
      </Button>
    );
  };

  const renderDefault = () => {
    return (
      <>
        <AppBar color="default" className="app-top-bar">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={step > 0 ? () => setStep(s => --s) : handleClose}
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography variant="h6" align="center" className="title">
              {sending ? <Loading circularProgressSize={20} /> : steps[step].title}
            </Typography>

            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Container maxWidth="sm" className="drawer-content d-flex flex-column">
          <Grid container>
            <Grid item xs={12}>
              <MobileStepper
                className="my-4"
                variant="progress"
                steps={steps.length + 2}
                position="top"
                activeStep={step + 1}
              />
            </Grid>

            <Grid item xs={12} className="wizard-content">
              {renderSteps()}
            </Grid>

            {steps[step].key !== stepsKeys.payment && (
              <Grid item xs={12} className="text-center">
                {renderButton()}
              </Grid>
            )}
          </Grid>
        </Container>
      </>
    );
  };

  const renderEcommerce = () => {
    return (
      <>
        <AppBar color="default">
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={step > 0 ? () => setStep(s => --s) : handleClose}
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography variant="h6" align="center" className={classes.title}>
              {steps[step].key === stepsKeys.client ? "Datos personales" : "Envío y medio de pago"}
            </Typography>

            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        {steps[step].key === stepsKeys.client && (
          <Container maxWidth="sm" className={classes.container}>
            <Grid container>
              <Grid item xs={12} className={classes.section}>
                {renderClient()}
              </Grid>

              <Grid item xs={12}>
                {renderButton()}
              </Grid>
            </Grid>
          </Container>
        )}
        {steps[step].key === stepsKeys.delivery && (
          <div className={classes.container}>
            <div container className={classes.deliveryAndPayment}>
              <Container maxWidth="sm">
                <div className={classes.section}>
                  <Typography variant="h6">{steps[1].title}</Typography>
                  <div>{renderDelivery()}</div>
                </div>

                <div className={classes.section}>
                  <Typography variant="h6">{steps[2].title}</Typography>
                  <div>{renderPayment(true)}</div>
                </div>
              </Container>
            </div>

            <div className={classes.summary}>
              {items.length > 1 ? (
                <div>
                  <Typography variant="body2" className={classes.strong}>
                    Resumen de compra
                  </Typography>
                </div>
              ) : (
                <div className={classes.singleProductItem}>
                  <Avatar variant="rounded" className={classes.productMedia}>
                    {items[0].product.medias && items[0].product.medias.length > 0 && (
                      <ImageLoader
                        className="cart-productImg"
                        imgKey={items[0].product.medias[0] && items[0].product.medias[0].key}
                        alt={items[0].product.name}
                        circularProgressSize={20}
                        thumb
                      />
                    )}
                  </Avatar>
                  <Typography variant="body1" align="center" color="textSecondary" className="mt-2">
                    {items[0].product.name}
                  </Typography>
                  <Typography variant="body2" align="center" color="textSecondary">
                    {((items[0].body || {}).features || []).length > 0 && (
                      <>
                        {items[0].body.features.map((f, i) => (
                          <span key={`f-${items[0].product.id}-${i}`}>
                            {f.label}: {f.option.label}
                            {i < items[0].body.features.length - 1 && ", "}
                          </span>
                        ))}
                        <br />
                      </>
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    align="center"
                    color="textSecondary"
                    className="mt-2 mb-3"
                  >
                    Cantidad: {items[0].quantity}
                  </Typography>
                </div>
              )}

              <div className={classes.summaryTotal}>
                <Grid container spacing={1}>
                  {(subtotal !== totalFinal || items.length > 1) && (
                    <>
                      <Grid item xs={6}>
                        {items.length > 1 ? (
                          <Typography variant="body2">Productos ({items.length})</Typography>
                        ) : (
                          <Typography variant="body2">Producto</Typography>
                        )}
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" align="right">
                          <Currency value={subtotal} />
                        </Typography>
                      </Grid>
                    </>
                  )}

                  {state.delivery.option === DELIVERY_OPTIONS.delivery &&
                    state.delivery.deliveryService &&
                    state.delivery.deliveryService.fee > 0 && (
                      <>
                        <Grid item xs={6}>
                          <Typography variant="body2">Envío</Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2" align="right">
                            <Currency value={state.delivery.deliveryService.fee} />
                          </Typography>
                        </Grid>
                      </>
                    )}

                  <Grid item xs={6}>
                    {items.length > 1 ? (
                      <Typography variant="body1" className={classes.strong}>
                        Total
                      </Typography>
                    ) : (
                      <Typography variant="body1" className={classes.strong}>
                        Pagas
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body1" align="right" className={classes.strong}>
                      <Currency value={totalFinal} />
                    </Typography>
                  </Grid>
                </Grid>
              </div>

              {renderButton()}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        className: "full-width"
      }}
    >
      {!mobile && template === TEMPLATES.ecommerce ? renderEcommerce() : renderDefault()}
    </Drawer>
  );
};

export default withContext(PaymentWizard);
